import React, { useState, useEffect } from 'react';
import { Download, Eye, CheckCircle, XCircle, Clock, FileText, X, CreditCard, Save } from 'lucide-react';
import { admissionsService } from '../../firebase/admissions';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const MyApplications = () => {
  const { currentUser } = useRealtime();
  const [applications, setApplications] = useState([]);
  const [testRegistrations, setTestRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTests, setLoadingTests] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundFormData, setRefundFormData] = useState({
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    branchName: ''
  });
  const [savingRefund, setSavingRefund] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadApplications();
      loadTestRegistrations();
    }
  }, [currentUser]);

  const loadTestRegistrations = async () => {
    try {
      setLoadingTests(true);
      const result = await admissionsService.getUserTestRegistrations(currentUser.uid);
      if (result.success) {
        // Load institution and test details for each registration
        const registrationsWithDetails = await Promise.all(
          (result.registrations || []).map(async (reg) => {
            const [instResult, testResult] = await Promise.all([
              admissionsService.getInstitutionById(reg.institutionId),
              reg.testId ? admissionsService.getAdmissionTestById(reg.testId) : Promise.resolve({ success: false })
            ]);
            return {
              ...reg,
              institutionName: instResult.success ? instResult.institution.name : 'Unknown Institution',
              testName: testResult.success ? testResult.test.testName : null
            };
          })
        );
        setTestRegistrations(registrationsWithDetails);
      }
    } catch (error) {
      console.error('Error loading test registrations:', error);
    } finally {
      setLoadingTests(false);
    }
  };

  const loadApplications = async () => {
    try {
      setLoading(true);
      const result = await admissionsService.getUserApplications(currentUser.uid);
      if (result.success) {
        setApplications(result.applications || []);
      } else {
        toast.error('Failed to load applications: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Error loading applications: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', label: 'Pending' },
      approved: { icon: CheckCircle, color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Admitted' },
      admitted: { icon: CheckCircle, color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Admitted' },
      rejected: { icon: XCircle, color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Rejected' },
      refund_pending: { icon: Clock, color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200', label: 'Refund Pending' },
      refunded: { icon: CheckCircle, color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Refunded' },
      submitted: { icon: Clock, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', label: 'Submitted' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`${badge.color} px-3 py-1 rounded-full text-sm flex items-center gap-1`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  const handleSaveRefundDetails = async (application) => {
    if (!refundFormData.accountHolderName || !refundFormData.accountNumber || !refundFormData.ifscCode || !refundFormData.bankName) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setSavingRefund(true);
      const result = await admissionsService.saveRefundRequest({
        applicationId: application.id,
        userId: currentUser.uid,
        studentName: `${application.firstName} ${application.lastName}`,
        studentId: currentUser.uid,
        institutionId: application.institutionId,
        institutionName: application.institutionName,
        transactionAmount: application.paymentAmount || 0,
        transactionId: application.paymentTransactionId,
        bankAccountDetails: refundFormData,
        status: 'pending'
      });

      if (result.success) {
        // Update application status to refund_pending
        await admissionsService.updateApplicationStatus(application.id, 'refund_pending', 'Bank account details submitted for refund');
        
        toast.success('Bank account details saved successfully. Refund will be processed within 7-10 working days.');
        setShowRefundForm(false);
        setRefundFormData({
          accountHolderName: '',
          accountNumber: '',
          ifscCode: '',
          bankName: '',
          branchName: ''
        });
        loadApplications();
      } else {
        toast.error('Failed to save refund details: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Error saving refund details: ' + error.message);
    } finally {
      setSavingRefund(false);
    }
  };

  const handleDownloadReceipt = async (application) => {
    try {
      // Load institution data
      const instResult = await admissionsService.getInstitutionById(application.institutionId);
      if (!instResult.success) {
        toast.error('Failed to load institution data');
        return;
      }

      const institution = instResult.institution;
      
      // Generate receipt
      const { generateApplicationReceipt } = await import('../../utils/receiptGenerator');
      await generateApplicationReceipt(
        application,
        institution,
        application.paymentTransactionId || 'N/A',
        {
          amount: application.paymentAmount || institution.applicationFee || 0,
          status: application.paymentStatus || 'completed'
        }
      );
      
      toast.success('Receipt downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate receipt: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Applications</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          View your admission test registrations and application statuses
        </p>
      </div>

      {/* Admission Test Registrations */}
      {testRegistrations.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-600" />
            Admission Test Registrations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {testRegistrations.map((reg) => (
              <div
                key={reg.id}
                className="border-2 border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-lg transition-shadow"
              >
                <div className="mb-3">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                    {reg.institutionName}
                  </h3>
                  {reg.testName && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {reg.testName}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Test Registered</span>
                  </div>
                  
                  {reg.testResult && (
                    <div className={`flex items-center gap-2 font-medium ${
                      reg.testResult === 'selected'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {reg.testResult === 'selected' ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          <span>Selected ✓</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4" />
                          <span>Not Selected</span>
                        </>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span>
                      {reg.registeredAt?.toDate?.()?.toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      }) || 'N/A'}
                    </span>
                  </div>

                  {reg.admitCardUrl ? (
                    <a
                      href={reg.admitCardUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mt-3"
                    >
                      <Download className="w-4 h-4" />
                      <span className="font-medium">Download Admit Card</span>
                    </a>
                  ) : (
                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-500 mt-3">
                      <Clock className="w-4 h-4" />
                      <span className="text-xs">Admit card pending</span>
                    </div>
                  )}

                  {!reg.hasApplied && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                        Complete your admission application
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Applications Section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Admission Applications</h2>
      </div>

      {applications.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">No applications found</p>
          <p className="text-gray-500 dark:text-gray-500 text-sm">
            You haven't submitted any applications yet
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Institution</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {applications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {app.institutionName || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {app.institutionType ? app.institutionType.charAt(0).toUpperCase() + app.institutionType.slice(1) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(app.status)}
                      {app.status === 'rejected' && (
                        <div className="mt-2">
                          <button
                            onClick={() => {
                              setSelectedApplication(app);
                              setShowRefundForm(true);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1 mt-1"
                          >
                            <CreditCard className="w-3 h-3" />
                            Submit Refund Details
                          </button>
                        </div>
                      )}
                      {(app.status === 'refund_pending' || app.status === 'refunded') && (
                        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                          {app.status === 'refund_pending' ? 'Processing refund...' : 'Refund completed'}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {app.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      ₹{app.paymentAmount || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            setSelectedApplication(app);
                            setShowDetails(true);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                          title="View Details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        {app.paymentTransactionId && (
                          <button
                            onClick={() => handleDownloadReceipt(app)}
                            className="text-green-600 hover:text-green-800"
                            title="Download Receipt"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Application Details Modal */}
      {showDetails && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Application Details</h2>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedApplication(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Personal Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Name:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">
                        {selectedApplication.firstName} {selectedApplication.lastName}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Email:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{selectedApplication.email}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Phone:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{selectedApplication.phone}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Date of Birth:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{selectedApplication.dateOfBirth || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {selectedApplication.previousSchool && (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Academic Information</h3>
                    <div className="text-sm space-y-1">
                      <p><span className="text-gray-600 dark:text-gray-400">Previous School:</span> {selectedApplication.previousSchool}</p>
                      <p><span className="text-gray-600 dark:text-gray-400">Previous Class:</span> {selectedApplication.previousClass}</p>
                      <p><span className="text-gray-600 dark:text-gray-400">Applying For:</span> {selectedApplication.applyingForClass}</p>
                    </div>
                  </div>
                )}

                {selectedApplication.paymentTransactionId && (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Payment Information</h3>
                    <div className="text-sm space-y-1">
                      <p><span className="text-gray-600 dark:text-gray-400">Transaction ID:</span> {selectedApplication.paymentTransactionId}</p>
                      <p><span className="text-gray-600 dark:text-gray-400">Amount:</span> ₹{selectedApplication.paymentAmount || 0}</p>
                      <p><span className="text-gray-600 dark:text-gray-400">Status:</span> {selectedApplication.paymentStatus || 'Completed'}</p>
                    </div>
                  </div>
                )}

                {selectedApplication.status === 'rejected' && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium mb-2">
                      Your application has been rejected. You will receive a refund within 7-10 working days.
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-3">
                      Please submit your bank account details to process the refund.
                    </p>
                    <button
                      onClick={() => {
                        setShowDetails(false);
                        setShowRefundForm(true);
                      }}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2 text-sm"
                    >
                      <CreditCard className="w-4 h-4" />
                      Submit Bank Account Details
                    </button>
                  </div>
                )}
                {selectedApplication.status === 'refund_pending' && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-4">
                    <p className="text-sm text-orange-800 dark:text-orange-200 font-medium mb-2">
                      Refund Pending
                    </p>
                    <p className="text-xs text-orange-700 dark:text-orange-300">
                      Your refund request is being processed. You will receive the refund within 7-10 working days.
                    </p>
                  </div>
                )}
                {selectedApplication.status === 'refunded' && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                    <p className="text-sm text-green-800 dark:text-green-200 font-medium mb-2">
                      Refund Completed
                    </p>
                    <p className="text-xs text-green-700 dark:text-green-300">
                      Your refund has been processed and transferred to your bank account.
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t">
                  {selectedApplication.paymentTransactionId && (
                    <button
                      onClick={() => handleDownloadReceipt(selectedApplication)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Receipt
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowDetails(false);
                      setSelectedApplication(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Refund Form Modal */}
      {showRefundForm && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Submit Bank Account Details</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Application: {selectedApplication.institutionName}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowRefundForm(false);
                    setSelectedApplication(null);
                    setRefundFormData({
                      accountHolderName: '',
                      accountNumber: '',
                      ifscCode: '',
                      bankName: '',
                      branchName: ''
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium mb-1">
                  Refund Information
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                  Your application has been rejected. You will receive a refund of ₹{selectedApplication.paymentAmount || 0} within 7-10 working days after submitting your bank account details.
                </p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Account Holder Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={refundFormData.accountHolderName}
                      onChange={(e) => setRefundFormData({ ...refundFormData, accountHolderName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter account holder name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Account Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={refundFormData.accountNumber}
                      onChange={(e) => setRefundFormData({ ...refundFormData, accountNumber: e.target.value.replace(/\D/g, '') })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter account number"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      IFSC Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={refundFormData.ifscCode}
                      onChange={(e) => setRefundFormData({ ...refundFormData, ifscCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                      placeholder="Enter IFSC code (e.g., SBIN0001234)"
                      maxLength={11}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bank Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={refundFormData.bankName}
                      onChange={(e) => setRefundFormData({ ...refundFormData, bankName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter bank name"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Branch Name
                    </label>
                    <input
                      type="text"
                      value={refundFormData.branchName}
                      onChange={(e) => setRefundFormData({ ...refundFormData, branchName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter branch name (optional)"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>Note:</strong> Please ensure all bank account details are correct. Incorrect details may delay the refund process.
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowRefundForm(false);
                      setSelectedApplication(null);
                      setRefundFormData({
                        accountHolderName: '',
                        accountNumber: '',
                        ifscCode: '',
                        bankName: '',
                        branchName: ''
                      });
                    }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSaveRefundDetails(selectedApplication)}
                    disabled={savingRefund}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4" />
                    {savingRefund ? 'Saving...' : 'Save & Submit'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyApplications;

